import { connect } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import { useListSearch } from "../../../common/hooks";
import TooltipButton from "../../../common/tooltipButton";
import { useIntl } from "react-intl";
import List from "../../../common/list";
import { TextInput } from "../../../common/fields";
import * as ListCellRenders from "../../../common/list/ListCellRenders";
import {
  listRequest,
  setQuery,
} from "../../../../store/slices/enterpriseSlice";
import * as RoleHelper from "../../../../helpers/RoleHelper";
import { AccessScopeConstants, RouteConstants } from "../../../../constants";
import { useNavigate } from "react-router-dom";
import CustomBreadCrumbs from "../../../common/breadcrumbs";

export const EnterpriseList = ({
  action,
  labels,
  list,
  listRequest,
  query,
  requesting,
  setQuery,
  user,
}) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [namePattern, setNamePattern] = useListSearch(
    query?.namePattern,
    (value) => setQuery({ namePattern: value, pageNo: 1 })
  );
  const [selectedRecords, setSelectedRecords] = useState([]);

  const { role } = user || {};

  const canAdd = RoleHelper.checkRoleScope(
    role?.scope,
    AccessScopeConstants.TenantAccess,
    AccessScopeConstants.CreateScope
  );

  const fetchList = useCallback(() => {
    listRequest(query);
  }, [query, listRequest]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onSelectionChange = (selectedRecords) => {
    setSelectedRecords(selectedRecords?.value || []);
  };

  const handlePageChange = (pageData) => {
    if (
      query.pageSize !== pageData.rows ||
      query.pageNo !== pageData.page + 1
    ) {
      setQuery({
        ...query,
        pageNo: pageData.page + 1,
        pageSize: pageData.rows,
      });
    }
  };

  const handleOnSortChange = (sortData) => {
    setQuery({
      ...query,
      pageNo: 1,
      sortColumn: sortData.sortField,
      sortBy: sortData.sortOrder === 1 ? "asc" : "desc",
    });
  };

  const handleAddEnterprise = () => {
    navigate(RouteConstants.APP_ROUTE_ENTERPRISES_ADD);
  };

  return (
    <div>
      <CustomBreadCrumbs labels={labels} navigate={navigate} />
      <div className="edexlms-page-content">
        <div className="edexlms-page-header">
          <div className="edexlms-page-title">
            {intl.formatMessage({ id: "enterprises" })}
          </div>
          <div className="edexlms-page-actions">
            <TooltipButton
              icon="pi pi-plus"
              onClick={handleAddEnterprise}
              size="small"
              label={intl.formatMessage({ id: "add_enterprise" })}
              type="button"
              disabled={!canAdd}
              tooltipOptions={{ showOnDisabled: true, position: "bottom" }}
              tooltip={
                canAdd ? "" : intl.formatMessage({ id: "no_permission" })
              }
            />
          </div>
        </div>
        <div className="edexlms-page-body">
          <div className="edexlms-search-filter-bar">
            <TextInput
              iconPosition="left"
              icon="pi-search"
              placeholder={intl.formatMessage({ id: "search_enterprises" })}
              input={{
                name: "search",
                value: namePattern,
                onChange: (e) => setNamePattern(e.target.value),
              }}
            />
          </div>
          <List
            loading={action === "list" && requesting}
            onSelectionChange={onSelectionChange}
            handlePageChange={handlePageChange}
            handleOnSortChange={handleOnSortChange}
            data={list || []}
            selectedRecords={selectedRecords}
            pageQuery={query}
            selectionMode=""
            columns={[
              {
                field: "name",
                header: "name",
                sortable: true,
                body: ListCellRenders.NameAndLogo(),
              },
              {
                field: "contactPersonName",
                header: "contact_person",
                sortable: true,
                body: ListCellRenders.NameAndEmail(
                  "contactPersonName",
                  "contactEmail"
                ),
              },
              // On and with a current licence (the API's isActive).
              {
                field: "isActive",
                header: "status",
                body: ListCellRenders.Status,
              },
              {
                field: "actions",
                headerIcon: "pi-cog",
                align: "center",
                body: ListCellRenders.OtherActions([
                  {
                    label: "edit",
                    icon: "pi-pencil",
                    isAllowed: true,
                    action: (data) =>
                      navigate(
                        `${RouteConstants.APP_ROUTE_ENTERPRISES_EDIT.replace(
                          ":id",
                          data.id
                        )}`
                      ),
                  },
                ]),
              },
            ]}
          />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  list: state?.enterprises?.data?.list || [],
  action: state?.enterprises?.action,
  requesting: state?.enterprises?.requesting,
  error: state?.enterprises?.error,
  query: state?.enterprises?.query,
  user: state?.self?.user,
});

const mapDispatchToProps = (dispatch) => ({
  listRequest: (data) => dispatch(listRequest(data)),
  setQuery: (data) => dispatch(setQuery(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EnterpriseList);
